import React, { useState } from 'react';
import axios from 'axios';
import './UploadModal.css';

function UploadModal({ onClose, onSuccess }) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [message, setMessage] = useState('');
  const [results, setResults] = useState(null);

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    const validFiles = [];

    for (const file of selectedFiles) {
      const name = file.name.toLowerCase();
      if (name.endsWith('.xlsx') || name.endsWith('.xls') || name.endsWith('.csv')) {
        validFiles.push(file);
      }
    }

    if (validFiles.length === 0) {
      setMessage('Please select Excel (.xlsx, .xls) or CSV (.csv) files');
      setFiles([]);
    } else if (validFiles.length !== selectedFiles.length) {
      setMessage(`Added ${validFiles.length} file(s) (${selectedFiles.length - validFiles.length} unsupported files skipped)`);
      setFiles(validFiles);
    } else {
      setMessage('');
      setFiles(validFiles);
    }
  };

  const handleUpload = async () => {
    if (files.length === 0) {
      setMessage('Please select Excel files');
      return;
    }

    try {
      setUploading(true);
      setResults(null);
      let totalInserted = 0;
      let totalFailed = 0;
      let totalDuplicates = 0;

      for (let i = 0; i < files.length; i++) {
        const formData = new FormData();
        formData.append('file', files[i]);
        setProgress({ current: i + 1, total: files.length });

        try {
          const response = await axios.post('/api/upload', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          totalInserted += response.data.inserted;
          totalFailed += response.data.failed;
          totalDuplicates += response.data.duplicates || 0;
        } catch (error) {
          console.error(`Failed to upload ${files[i].name}:`, error);
          totalFailed += 1;
        }
      }

      const summaryResults = {
        inserted: totalInserted,
        failed: totalFailed,
        duplicates: totalDuplicates,
        filesProcessed: files.length
      };

      setResults(summaryResults);
      setMessage(`✓ Bulk upload complete!`);
      setTimeout(() => {
        onSuccess();
      }, 2000);
    } catch (error) {
      setMessage(`✗ Failed: ${error.response?.data?.error || error.message}`);
    } finally {
      setUploading(false);
      setProgress({ current: 0, total: 0 });
    }
  };

  const removeFile = (index) => {
    setFiles(files.filter((_, i) => i !== index));
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>📤 Bulk Upload Excel Files</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="modal-body">
          <div className="upload-area">
            <input 
              type="file" 
              multiple
              accept=".xlsx,.xls,.csv"
              onChange={handleFileChange}
              disabled={uploading}
              id="file-input"
            />
            <label htmlFor="file-input" className="upload-label">
              {files.length > 0 ? `📁 ${files.length} file(s) selected` : '📎 Click to select Excel or CSV files (multiple)'}
            </label>
          </div>

          {files.length > 0 && (
            <div className="file-list">
              <h4>Selected Files:</h4>
              <ul>
                {files.map((file, index) => (
                  <li key={index}>
                    <span>{file.name}</span>
                    {!uploading && (
                      <button 
                        className="btn-remove" 
                        onClick={() => removeFile(index)}
                      >
                        ✕
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {uploading && progress.total > 0 && (
            <div className="progress-container">
              <div className="progress-bar">
                <div 
                  className="progress-fill" 
                  style={{ width: `${(progress.current / progress.total) * 100}%` }}
                ></div>
              </div>
              <p className="progress-text">
                Processing {progress.current} of {progress.total} files...
              </p>
            </div>
          )}

          {message && (
            <div className={`message ${message.includes('✓') ? 'success' : 'error'}`}>
              {message}
            </div>
          )}

          {results && (
            <div className="results-summary">
              <h4>📊 Upload Summary</h4>
              <div className="summary-item">
                <span>Files Processed:</span>
                <strong>{results.filesProcessed}</strong>
              </div>
              <div className="summary-item success-item">
                <span>✓ Companies Added:</span>
                <strong>{results.inserted}</strong>
              </div>
              <div className="summary-item">
                <span>⚠ Duplicates Skipped:</span>
                <strong>{results.duplicates}</strong>
              </div>
              {results.failed > 0 && (
                <div className="summary-item error-item">
                  <span>✗ Failed:</span>
                  <strong>{results.failed}</strong>
                </div>
              )}
            </div>
          )}

          <div className="modal-footer">
            <button 
              className="btn btn-secondary" 
              onClick={onClose}
              disabled={uploading}
            >
              Cancel
            </button>
            <button 
              className="btn btn-primary" 
              onClick={handleUpload}
              disabled={files.length === 0 || uploading}
            >
              {uploading ? '⏳ Uploading...' : `⬆️ Upload ${files.length > 0 ? `(${files.length})` : ''}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default UploadModal;
